<template>
  <div class="new-criterio-content">
    <div class="title-criterio-details">{{ title }}</div>
    <v-card class="new-criterio" elevation="0" :disabled="disabled">
      <v-card-text>
        <v-tabs v-model="tabIndex" background-color="transparent" hide-slider grow class="tabs-menu">
          <v-tab v-for="tab in criterias.children" :key="tab.id" class="tab-container" @change="changeTab(tab.children)">
            {{ tab.label }}
            <news-badge v-if="isNewSegmentCriterias(tab.label)" class="pl-1" />
          </v-tab>
        </v-tabs>
        <div class="segmentation-criteria-group">
          <v-tabs-items v-for="group in groups" :key="group.id">
            <p class="group-title">{{ group.label }}</p>
            <div v-for="(criterio, index) in group.children" :key="index" class="criterio-template">
              <div v-if="!criterio.hidden">
                <v-menu v-if="getSafe(() => segmentationGroups.groups.length) >= 2">
                  <template v-slot:activator="scope">
                    <div v-on:dblclick.once="scope.on.click" @dblclick="clickedCriteria = criterio">
                      <v-card
                        class="criterio-container"
                        draggable
                        :class="{ dragged: draggedObject.id === index && group.id === groupId }"
                        @dragstart="drag(criterio, group)"
                        @dragend="dragEnd"
                      >
                        <mf-icon small color="#C3C8CD">drag_indicator</mf-icon>
                        <span class="criterio-label">
                          {{ criterio.label }}
                          <news-badge v-if="isNewSegmentCriterias(`${criterias.children[tabIndex].label}.${group.label}.${criterio.label}`)" class="badges" />
                        </span>
                        <mf-icon class="arrow-icon-right" size="12" color="#78838E">arrow_forward_ios</mf-icon>
                      </v-card>
                    </div>
                  </template>

                  <v-list>
                    <v-list-item
                      v-for="(groupToSelect, index) in segmentationGroups.groups"
                      :key="index"
                      @click="addCriteriaOnGroup(clickedCriteria, group, index)"
                    >
                      <v-list-item-title>Grupo {{ index + 1 }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-card
                  v-else
                  class="criterio-container"
                  draggable
                  :class="{ dragged: draggedObject.id === index && group.id === groupId }"
                  @dragstart="drag(criterio, group)"
                  @dragend="dragEnd"
                  @dblclick="addCriteriaOnGroup(criterio, group, 0)"
                >
                  <mf-icon small color="#C3C8CD">drag_indicator</mf-icon>
                  <span class="criterio-label">
                    {{ criterio.label }}
                    <news-badge v-if="isNewSegmentCriterias(`${criterias.children[tabIndex].label}.${group.label}.${criterio.label}`)" class="badges" />
                  </span>
                  <mf-icon class="arrow-icon-right" size="12" color="#78838E">arrow_forward_ios</mf-icon>
                </v-card>
              </div>
            </div>
          </v-tabs-items>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import { isNewSegmentCriterias } from '@/helpers/news.js'

import { leftBranch } from '@/helpers/segmentationTree'
import cloneDeep from 'lodash/cloneDeep'
export default {
  components: {
    NewsBadge: () => import('@/components/NewsBadge.vue'),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Critérios da Segmentação',
    },
  },
  data: () => ({
    localGroups: [],
    group: null,
    clickedCriteria: null,
  }),
  computed: {
    ...mapState({
      draggedObject: (state) => state.segmentation.draggedObject,
      selectedCondition: (state) => state.segmentation.selectedCondition,
      criterias: (state) => state.segmentation.segmentation,
      segmentationGroups: (state) => state.segmentation.segmentationGroups,
      segmentationTabIndex: (state) => state.segmentation.segmentationTabIndex,
    }),

    tabIndex: {
      get() {
        return this.segmentationTabIndex
      },
      set(value) {
        this.setSegmentationTabIndex(value)
      },
    },
    groups: {
      get() {
        return this.localGroups
          .filter((group) => !group.requires_pii_access || this.hasPiiAccess)
          .filter((group) => !group.hidden)
          .filter((group) => group.children?.filter((child) => !child.hidden).length > 0)
      },
      set(groups) {
        this.localGroups = groups
      },
    },
  },
  mounted() {
    this.groups = this.criterias.children[this.tabIndex].children
  },
  methods: {
    ...mapWaitingActions({
      setDraggedObject: 'set-dragged-object',
      setSelectedCriteria: 'set-selected-criteria',
      setSegmentationGroups: 'set-segmentation-groups',
      setSegmentationTabIndex: 'set-segmentation-tab-index',
    }),
    ...mapActions(['setPersistOriginalSize']),
    isNewSegmentCriterias,
    drag(criterio, group) {
      this.group = group
      const newLeftBranch = leftBranch(criterio.children[0]).map((node) => ({
        id: node.id,
        value: node.value,
      }))

      const newCriteriaValue = [{ id: this.criterias.children[this.tabIndex].id }, { id: group.id }, { id: criterio.id }]

      const draggedObject = { criteriaValue: newCriteriaValue.concat(newLeftBranch), new: true }

      this.setDraggedObject(draggedObject)
    },

    dragEnd() {
      this.group = null
      this.setDraggedObject({})
    },

    addCriteriaOnGroup(criterio, criteriaGroup, groupIndex) {
      this.group = criteriaGroup
      const newLeftBranch = leftBranch(criterio.children[0]).map((node) => ({
        id: node.id,
        value: node.value,
      }))

      const newCriteriaValue = [{ id: this.criterias.children[this.tabIndex].id }, { id: criteriaGroup.id }, { id: criterio.id }]

      const criterioToAdd = { criteriaValue: newCriteriaValue.concat(newLeftBranch), new: true }

      this.setSelectedCriteria({ ...criterioToAdd, groupIndex, criteriaIndex: this.getIndexToInsertCriteria(groupIndex) })

      this.insertCriteriaOnGroup(criterioToAdd.criteriaValue, groupIndex, this.getIndexToInsertCriteria(groupIndex))
    },

    getIndexToInsertCriteria(groupIndex) {
      const branch = this.segmentationGroups.groups[groupIndex]

      if (branch?.criteria?.length) return branch.criteria.length
      else return 0
    },

    insertCriteriaOnGroup(branch, groupIndex, criteriaIndex) {
      let segmentationGroups = cloneDeep(this.segmentationGroups)
      if (groupIndex >= segmentationGroups?.groups?.length) {
        segmentationGroups.groups[groupIndex] = {
          criteria: [],
          operator: 'AND',
        }
      }

      segmentationGroups.groups[groupIndex].criteria[criteriaIndex] = branch
      this.setSegmentationGroups(segmentationGroups)
      this.setPersistOriginalSize(false)
    },

    changeTab(groups) {
      this.groups = groups
    },
  },
}
</script>

<style lang="scss" scoped>
.new-criterio-content {
  .instructions-container {
    background-color: $marca-0-l30;
    border-radius: 10px;
    padding: 10px;
    margin: 0 20px !important;
    color: $branco;
    font-size: 14px;
    font-weight: bold;
    align-items: center;
  }

  .new-criterio {
    width: 100%;
    display: inline-flex;
    padding: 20px;
    border-radius: 8px;
    background: #ecedf0;

    .v-card__text {
      padding-top: 0;
    }

    .tabs-menu {
      border-bottom: 1px solid $cinza-l60;
    }
    .segmentation-criteria-group {
      height: 450px;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 0px 8px 0px 4px;

      ::v-deep .v-window__container {
        background-color: $cinza-l80;
      }
    }

    .tab-container {
      padding: 10px 16px;
      border-bottom: 3px transparent solid;
      text-align: center;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;

      ::v-deep .v-slide-group__next,
      ::v-deep .v-slide-group__next {
        max-width: 35px;
      }

      &.v-tab {
        color: $marca-0;
        opacity: 0.6333;
        letter-spacing: normal;
      }

      &.v-tab--active {
        color: $marca-0;
        opacity: 0.6333;
        border-bottom: 3px $marca-0-l40 solid;
        background-color: transparent;
        letter-spacing: normal;
      }
    }
    .group-title {
      color: #a3a3a3;
      font-size: 12px;
      font-weight: 700;
      line-height: normal;
      text-transform: uppercase;
      margin: 20px 0px 12px 0px;
    }

    .criterio-container {
      height: 28px;
      border-radius: 2px;
      border: 1px solid $cinza-l60;
      background: $branco;
      cursor: move;
      display: flex;
      align-items: center;
      padding: 6px 11px 6px 3px;
      box-shadow: none !important;
    }

    .criterio-label {
      width: 100%;
      color: $cinza-d20;
      font-size: 12px;
      font-weight: 400;
      line-height: normal;
      margin-left: 9px;
    }

    .badges {
      display: inline;
    }

    .criterio-template {
      text-align: left;
      margin-bottom: 4px;

      ::v-deep .v-card__text {
        height: 28px;
      }

      button {
        padding: 0;
        height: 28px;
        width: 100%;

        .v-card {
          width: 100%;
        }
      }

      .v-btn {
        text-transform: none;
      }
    }

    .dragged {
      background-color: $cinza-l80 !important;
    }
  }
  .title-criterio-details {
    color: $cinza-d20;
    font-size: 18px;
    font-weight: 400;
    line-height: 120%;
    margin: 6px 0px 36px 0px;
  }
}
</style>
